import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ROUTES } from 'components/Root'
import { useHistory } from 'react-router-dom'
import Modal from 'particles/Modal'
import { useTranslation } from 'react-i18next'
import CreationMethodCard from './CreationMethodCard'
import ObjectiveForm from 'components/Campaign/Template/ObjectiveForm'
import { ObjectiveType, OBJECTIVE } from 'state/campaignTemplates'
import { setTemplateFilter, clearTemplateFilter, clearTemplateData } from 'state/campaignTemplates'
import { CampaignManagement, TemplatedCampaignManagement } from 'components/Shared/Permission/enums'
import { useCheckSomePermissions } from 'hooks/permissions'
import { emitShowTooltip } from 'services/tooltipEmitter'
import { selectProfile } from 'state'
import { selectIsHelpdeskSupportedByActiveTeam } from 'state/filter'
import { useLocalStorage } from 'hooks'

interface SetupModalProps {
    onClose: () => void
}

const SetupModal: React.FC<SetupModalProps> = ({ onClose }) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const profile = useSelector(selectProfile)
    const isHelpdeskSupported = useSelector(selectIsHelpdeskSupportedByActiveTeam)
    const [helpdeskTooltipValue, setHelpdeskTooltipValue] = useLocalStorage<Record<string, string>>(
        'helpdesk-tooltip',
        {},
    )

    const [showObjectiveForm, setShowObjectiveForm] = useState<boolean>(false)
    const hasCreateNewCampaignFromEditorPermission = useCheckSomePermissions([
        CampaignManagement.CreateAndEditBriefsForInternal,
        CampaignManagement.CreateAndEditBriefsForExternal,
    ])
    const hasCreateNewCampaignFromTemplatePermission = useCheckSomePermissions([
        TemplatedCampaignManagement.CreateAndEditBriefsForInternal,
        TemplatedCampaignManagement.CreateAndEditBriefsForExternal,
    ])
    const skipSetupModal = hasCreateNewCampaignFromTemplatePermission && !hasCreateNewCampaignFromEditorPermission
    const navOnClick = (e: React.MouseEvent<HTMLDivElement>, direction: string) => {
        e.preventDefault()
        history.push(direction)
    }

    const objectiveComfirmOnClick = (objectiveType: ObjectiveType) => {
        if (isHelpdeskSupported && !!profile.uuid && !helpdeskTooltipValue[profile.uuid]) {
            emitShowTooltip('helpdesk')
            setHelpdeskTooltipValue({
                ...helpdeskTooltipValue,
                [profile.uuid]: '1',
            })
        }
        dispatch(clearTemplateFilter())
        dispatch(clearTemplateData())
        dispatch(
            setTemplateFilter({
                objective: OBJECTIVE[objectiveType],
            }),
        )
        history.push(`${ROUTES.Campaign}/template/templateSelector?drawer=preview`)
    }

    return showObjectiveForm || skipSetupModal ? (
        <ObjectiveForm
            onClick={(objectiveType: ObjectiveType) => objectiveComfirmOnClick(objectiveType)}
            onClose={onClose}
        />
    ) : (
        <Modal
            size={'m'}
            title={t('campaign.creationModal.title')}
            onClose={() => {
                onClose()
            }}
            onConfirm={() => {
                onClose()
            }}
            hasButtonSet={false}
            hasIcon={false}
        >
            <>
                {hasCreateNewCampaignFromTemplatePermission && (
                    <CreationMethodCard
                        title={t('campaign.creationModal.createFromTemplate.title')}
                        description={t('campaign.creationModal.createFromTemplate.subTitle')}
                        iconType={'template'}
                        tag={{
                            label: t('campaign.creationModal.createFromTemplate.betaTag'),
                            tooltipContent: t('campaign.creationModal.createFromTemplate.betaTag.tooltips'),
                            iconName: 'info',
                        }}
                        onClick={() => {
                            setShowObjectiveForm(true)
                        }}
                        data-testid="test-create-from-template-button"
                    />
                )}

                {hasCreateNewCampaignFromEditorPermission && (
                    <CreationMethodCard
                        title={t('campaign.creationModal.createFromScratch.title')}
                        description={t('campaign.creationModal.createFromScratch.subTitle')}
                        iconType={'scratch'}
                        onClick={e => navOnClick(e, `${ROUTES.Campaign}/edit/registration`)}
                        data-testid="test-create-from-scratch-button"
                    />
                )}
            </>
        </Modal>
    )
}

export default SetupModal
